import React, { useState, memo, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { fetchSurvey } from "ducks/actions";
import {
  makeSelectIsFetchingData,
  makeSelectSurvey,
  makeSelectAppSettings
} from "ducks/selectors";
import Spinner, { styles } from "components/Spinner";
import FormattedMessage from "components/FormattedMessage";
import { urlParam, sanitizeHTML } from "utils/helpers";
import { Button, Flex, Box, Text } from "ui-components";
import map from "lodash/map";
import get from "lodash/get";
import filter from "lodash/filter";
import startCase from "lodash/startCase";
import toLower from "lodash/toLower";
import includes from "lodash/includes";
import axios from "axios";
import {
  OMS_URL,
  OMS_VERSION_PATH,
  OMS_SUBMIT_SURVEY,
  DEFAULT_API_VERSION,
  BASE
} from "utils/constants";
import { Label, Input, Textarea, Radio, Checkbox } from "@rebass/forms";

const SUBMIT_SURVEY_URL = `${OMS_URL}${OMS_VERSION_PATH}${OMS_SUBMIT_SURVEY}`.replace(
  "{VERSION}",
  DEFAULT_API_VERSION[BASE.OMS]
);

export async function submitSurvey(formData) {
  let apiRequest = await axios.post(SUBMIT_SURVEY_URL, {
    answers: formData,
    source: urlParam("source"),
    id: urlParam("id")
  });

  return apiRequest.data.results;
}

const Survey = ({ survey, fetchSurvey, isFetchingSurvey, ...props }) => {
  const [formData, setFormValue] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const introduction = get(survey, "introduction");

  useEffect(
    () => {
      fetchSurvey({ id: urlParam("id") });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (submitted) {
    const { image, manageorder } = props.appSettings.survey;
    return (
      <>
        <Text
          sx={{ mb: 4, textAlign: "center", fontSize: 19, fontWeight: "bold" }}
        >
          {startCase(toLower(survey.name))} Survey
        </Text>
        <Box
          variant="main"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: 15,
            flexDirection: "column"
          }}
          padding={[6, 8]}
        >
          <img width="45%" src={image} alt="survey" />
          <Text
            sx={{
              fontSize: "1rem",
              lineHeight: 1.5,
              marginTop: 20,
              marginBottom: 15
            }}
          >
            {survey.after_survey_content}
            <span
              role="img"
              aria-label="smiley"
              style={{ fontSize: 18, position: "absolute" }}
            >
              😊
            </span>
          </Text>
          <Button
            onClick={() => {
              window.location.href = manageorder;
            }}
          >
            <FormattedMessage id="manager_order_page_label" />
          </Button>
        </Box>
      </>
    );
  }

  return isFetchingSurvey ? (
    <Spinner size={200} sx={styles.fullPage} />
  ) : (
    <>
      <Text sx={{ mb: 4, textAlign: "center", fontSize: 4, fontWeight: 700 }}>
        {startCase(toLower(survey.name))} Survey
      </Text>

      <Box
        as="form"
        variant="main"
        margin="15px"
        padding={[6, 8]}
        onSubmit={e => {
          e.preventDefault();

          const blankAnswers = filter(
            map(survey.questions, question => question.id),
            questionId =>
              !formData[questionId] || formData[questionId].length <= 0
          );

          if (blankAnswers.length <= 0) {
            submitSurvey(formData);
            setSubmitted(true);
          } else {
            setErrors(blankAnswers);
          }
        }}
      >
        <Text sx={{ mb: 8, fontSize: 1, fontWeight: 700 }}>
          <FormattedMessage id="mandatory_fields" />
        </Text>

        {introduction && (
          <Text sx={{ mb: 8, fontWeight: 700 }}>{introduction}</Text>
        )}

        {renderQuestions(survey.questions, formData, setFormValue, errors)}

        <Flex justifyContent="center">
          <Button>
            <FormattedMessage id="submit" />
          </Button>
        </Flex>
      </Box>
    </>
  );
};

const mapStateToProps = createStructuredSelector({
  isFetchingSurvey: makeSelectIsFetchingData("survey"),
  survey: makeSelectSurvey(),
  appSettings: makeSelectAppSettings()
});

export const mapDispatchToProps = dispatch => {
  return {
    fetchSurvey: params => dispatch(fetchSurvey(params)),
    submitSurvey: payload => dispatch(submitSurvey(payload))
  };
};

const renderQuestions = (questions, formData, setFormValue, errors) => {
  return (
    <ol style={{ listStyle: "decimal" }}>
      {map(questions, question =>
        renderQuestion(question, formData, setFormValue, errors)
      )}
    </ol>
  );
};

const renderQuestion = (question, formData, setFormValue, errors) => {
  const hasError = includes(errors, question.id);

  return (
    <Flex flexDirection="column" mb={8} as="li" key={`question-${question.id}`}>
      <Text color={hasError ? "red" : "gray.5"} sx={{ lineHeight: 1.4, mb: 3 }}>
        {question.question_text}
      </Text>
      {renderAnswerOptions(
        question,
        question.answer_options.split("\n"),
        formData,
        setFormValue,
        hasError
      )}
    </Flex>
  );
};

const renderAnswerOptions = (
  question,
  answerOptions,
  formData,
  setFormValue,
  hasError
) => {
  return (
    <Flex
      sx={{
        flexDirection:
          question.type === "Rapidfire::Questions::Checkbox" ? "column" : "row"
      }}
    >
      {map(answerOptions, answerOption => (
        <AnswerOption
          key={`question-${question.id}-${answerOption}`}
          question={question}
          answerOption={answerOption}
          formData={formData}
          setFormValue={setFormValue}
          hasError={hasError}
        />
      ))}
    </Flex>
  );
};

const AnswerOption = ({
  question,
  answerOption,
  formData,
  setFormValue,
  hasError
}) => {
  const handleInputChange = data => {
    setFormValue({ ...formData, ...data });
  };

  const handleCheckboxChange = (target, questionId) => {
    const { checked, value } = target;

    if (checked) {
      const selectedAnswers =
        formData[questionId] && formData[questionId].split("\r\n");
      const newSelectedAnswers = selectedAnswers
        ? [...selectedAnswers, value]
        : [value];
      const answerString = newSelectedAnswers.join("\r\n");
      setFormValue({ ...formData, [questionId]: answerString });
    } else {
      const selectedAnswers = formData[questionId].split("\r\n");
      const newSelectedAnswers = selectedAnswers.filter(
        answer => answer !== value
      );
      const answerString = newSelectedAnswers.join("\r\n");
      setFormValue({ ...formData, [questionId]: answerString });
    }
  };

  return (
    <>
      {question.type === "Rapidfire::Questions::Long" && (
        <Textarea
          onChange={e => handleInputChange({ [question.id]: e.target.value })}
          name={`q-${question.id}`}
          rows="6"
          width={[1]}
          sx={{ borderRadius: 8, borderColor: hasError ? "red" : "gray.0" }}
        />
      )}

      {question.type === "Rapidfire::Questions::Short" && (
        <Input
          onChange={e => handleInputChange({ [question.id]: e.target.value })}
          name={`q-${question.id}`}
          sx={{ borderRadius: 8 }}
        />
      )}

      {question.type === "Rapidfire::Questions::Radio" && (
        <Label
          sx={{
            alignItems: "center",
            flexDirection: "column"
          }}
        >
          <Radio
            color={hasError ? "red" : "#0000FF"}
            name={`q-${question.id}`}
            onChange={e =>
              handleInputChange({
                [question.id]: answerOption.replace(/[\n\r]+/g, "")
              })
            }
          />
          <Flex
            sx={{
              color: hasError ? "red" : "gray.5",
              mt: 2,
              mr: 2,
              alignItems: "center",
              flexDirection: "column",
              textAlign: "center"
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(answerOption) }}
          />
        </Label>
      )}

      {question.type === "Rapidfire::Questions::Checkbox" && (
        <Label
          sx={{
            alignItems: "center",
            flexDirection: "row",
            mt: 2
          }}
        >
          <Checkbox
            color={hasError ? "red" : "#0000FF"}
            name={`q-${question.id}`}
            value={answerOption.replace(/[\n\r]+/g, "")}
            onChange={e => {
              handleCheckboxChange(e.target, question.id);
            }}
          />
          <Flex
            sx={{
              color: hasError ? "red" : "gray.5",
              mr: 2,
              flexDirection: "column",
              textAlign: "center"
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(answerOption) }}
          />
        </Label>
      )}
    </>
  );
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withConnect, memo)(Survey);
